"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { SimilarJob } from "../types/job";
export function useWatchedJobs(normalizer) {
  const $ = _c(16);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    t1 = {
      initializeWithValue: false
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const [raw, setRaw] = useLocalStorage("watchedJobs", t0, t1);
  const didMigrate = useRef(false);
  let t2;
  if ($[2] !== normalizer || $[3] !== raw) {
    t2 = normalizeWatchedJobs(raw, normalizer);
    $[2] = normalizer;
    $[3] = raw;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const normalized = t2;
  let t3;
  let t4;
  if ($[5] !== normalized || $[6] !== raw || $[7] !== setRaw) {
    t3 = () => {
      if (!didMigrate.current && raw.length > 0) {
        didMigrate.current = true;
        setRaw(normalized);
      }
    };
    t4 = [raw, setRaw, normalized];
    $[5] = normalized;
    $[6] = raw;
    $[7] = setRaw;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== normalizer || $[11] !== setRaw) {
    t5 = updater => {
      if (typeof updater === "function") {
        setRaw(previous => {
          const current = normalizeWatchedJobs(previous, normalizer);
          return updater(current);
        });
      } else {
        setRaw(updater);
      }
    };
    $[10] = normalizer;
    $[11] = setRaw;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const setWatchedJobs = t5;
  let t6;
  if ($[13] !== normalized || $[14] !== setWatchedJobs) {
    t6 = [normalized, setWatchedJobs];
    $[13] = normalized;
    $[14] = setWatchedJobs;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6 as const;
}

/**
 * Takes the raw parsed localStorage value (could be anything) and returns
 * a clean, deduplicated SimilarJob[].
 */
export function normalizeWatchedJobs<TSimilarJob extends SimilarJob = SimilarJob>(raw: unknown, normalizer?: (similarJob: SimilarJob) => TSimilarJob): TSimilarJob[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const result: TSimilarJob[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) {
      continue;
    }
    const entry = normalizeEntry(item as Record<string, unknown>);
    if (entry && !seen.has(entry.idClient)) {
      seen.add(entry.idClient);
      result.push(normalizer ? normalizer(entry) : entry as TSimilarJob);
    }
  }
  return result;
}

/**
 * Normalizes a single raw localStorage entry (which may be a legacy JobData
 * with `locations[]` or an already-migrated SimilarJob with flat `city`) into
 * the canonical SimilarJob shape.
 */
function normalizeEntry(raw: Record<string, unknown>): SimilarJob | null {
  const idClient = raw.idClient;
  const title = raw.title;
  if (typeof idClient !== "string" || typeof title !== "string") {
    return null;
  }
  const locations = raw.locations as {
    city?: string;
    country?: string;
  }[] | undefined;
  const city = typeof raw.city === "string" ? raw.city : locations?.[0]?.city ?? "";
  const country = typeof raw.country === "string" ? raw.country : locations?.[0]?.country ?? "";
  const jobField = Array.isArray(raw.jobField) ? raw.jobField[0] : raw.jobField;
  return {
    idClient,
    title,
    city,
    country,
    company: typeof raw.company === "string" ? raw.company : "",
    entryLevel: typeof raw.entryLevel === "string" ? raw.entryLevel : "",
    postingDate: typeof raw.postingDate === "string" ? raw.postingDate : "",
    jobField: typeof jobField === "string" ? jobField : "",
    applicationUrl: typeof raw.applicationUrl === "string" ? raw.applicationUrl : "",
    source: typeof raw.source === "string" ? raw.source : undefined
  };
}